import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Check, Info } from 'lucide-react'
import type { ConnectorType } from '@/types'
import { Separator } from '@/components/ui/separator'
import { useGitHubDeviceFlow } from '@/hooks/useGitHubDeviceFlow'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { CONNECTOR_LABELS, CONNECTOR_TOKEN_LABELS, supportsMultipleInstances } from './connectorConstants'
import { CopilotDeviceFlowPanel } from './CopilotDeviceFlowPanel'
import { CONNECTOR_ICONS } from './connectorIcons'

const TOKEN_HELP_TEXT: Partial<Record<ConnectorType, string>> = {
  GitHub: 'Go to GitHub > Settings > Developer settings > Personal access tokens > Fine-grained tokens, select the repositories you need, and grant Contents (read/write) and Pull requests (write) permissions.',
  GitLab: 'Go to GitLab > Preferences > Access Tokens and create a token with api scope.',
  Bitbucket: 'Go to id.atlassian.com > Security > API tokens and create a token with scopes: read:user:bitbucket, read:workspace:bitbucket, read:repository:bitbucket, write:repository:bitbucket, read:pullrequest:bitbucket, write:pullrequest:bitbucket.',
  AzureDevOps: 'Go to Azure DevOps > User settings > Personal access tokens and create a token with Code (Read) scope.',
  Jira: 'Go to id.atlassian.com > Security > API tokens, create a token with read:jira-user scope, and paste it above along with your Atlassian email.',
  Figma: 'Go to Figma > Settings > Security > Personal access tokens and create a token with at least Current user (read) and File content (read) access.',
  Anthropic: 'Create an API key at console.anthropic.com > API Keys. It is checked with Anthropic when you connect; a key that does not work is not saved.',
  GitHubCopilot: 'Paste a fine-grained personal access token (starts with github_pat_) from an account with an active Copilot subscription. It is checked with GitHub when you connect.',
  QubizTitan: 'Paste the personal key you received from the Titan platform team. Titan can only be reached from your organization\'s run environment, so the key is checked on the first Titan run in this project.',
}

interface FormValues {
  connectionName: string
  token: string
  email: string
  site: string
}

function CopilotSignIn({ projectId, onComplete }: { projectId: string; onComplete: () => void }) {
  const deviceFlow = useGitHubDeviceFlow(projectId, onComplete)

  return (
    <div className="space-y-4">
      <CopilotDeviceFlowPanel state={deviceFlow.state} onInitiate={deviceFlow.initiate} onReset={deviceFlow.reset} />
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <Separator className="flex-1" />
        <span>or paste a token</span>
        <Separator className="flex-1" />
      </div>
    </div>
  )
}

interface TokenDialogProps {
  projectId: string
  connectorType: ConnectorType | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (connectionName: string, token: string, email?: string, site?: string) => void
  isSaving: boolean
  inlineError?: string | null
  connectionNameError?: string | null
  reconnectConnectionName?: string
}

export function TokenDialog({
  projectId,
  connectorType,
  open,
  onOpenChange,
  onSave,
  isSaving,
  inlineError,
  connectionNameError,
  reconnectConnectionName,
}: TokenDialogProps) {
  const isReconnect = reconnectConnectionName !== undefined
  const asksForConnectionName = !isReconnect && connectorType !== null && supportsMultipleInstances(connectorType)
  const isJira = connectorType === 'Jira'
  const isAzureDevOps = connectorType === 'AzureDevOps'
  const isBitbucket = connectorType === 'Bitbucket'
  const needsEmail = isJira || isAzureDevOps || isBitbucket
  const requiredMark = asksForConnectionName ? <span className="text-destructive" aria-hidden="true"> *</span> : null
  const tokenLabel = connectorType ? CONNECTOR_TOKEN_LABELS[connectorType] ?? 'Token' : 'Token'
  const helpText = connectorType ? TOKEN_HELP_TEXT[connectorType] : undefined

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: { connectionName: '', token: '', email: '', site: '' },
  })

  useEffect(() => {
    reset({ connectionName: '', token: '', email: '', site: '' })
  }, [connectorType, reset])

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) reset({ connectionName: '', token: '', email: '', site: '' })
    onOpenChange(nextOpen)
  }

  const onSubmit = (data: FormValues) => {
    const connectionName = reconnectConnectionName ?? data.connectionName
    if (isJira) {
      onSave(connectionName, data.token, data.email, data.site)
    } else if (needsEmail) {
      onSave(connectionName, data.token, data.email)
    } else {
      onSave(connectionName, data.token)
    }
  }

  if (!connectorType) return null

  const ConnectorIcon = CONNECTOR_ICONS[connectorType]

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className={asksForConnectionName ? 'flex-row items-center gap-3 space-y-0' : undefined}>
          {asksForConnectionName && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <ConnectorIcon className="h-5 w-5" />
            </div>
          )}
          <DialogTitle>
            {isReconnect
              ? `Reconnect ${CONNECTOR_LABELS[connectorType]}`
              : asksForConnectionName
                ? 'Add new connector'
                : `Connect ${CONNECTOR_LABELS[connectorType]}`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {connectorType === 'GitHubCopilot' && (
            <CopilotSignIn
              projectId={projectId}
              onComplete={() => {
                toast.success('GitHub Copilot connected successfully')
                handleClose(false)
              }}
            />
          )}

          {asksForConnectionName && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="connectionName">Connection Name{requiredMark}</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="Connection name requirements"
                        className="text-muted-foreground"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Must be unique for this connector type.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="connectionName"
                data-testid="token-dialog-connection-name"
                type="text"
                placeholder={`e.g. My ${CONNECTOR_LABELS[connectorType]}`}
                {...register('connectionName', { required: true })}
              />
              {connectionNameError && (
                <p role="alert" className="text-sm text-destructive">{connectionNameError}</p>
              )}
            </div>
          )}

          {isAzureDevOps && (
            <div className="space-y-2">
              <Label htmlFor="email">Organization{requiredMark}</Label>
              <Input
                id="email"
                data-testid="token-dialog-organization"
                type="text"
                placeholder="my-organization"
                {...register('email', { required: true })}
              />
              <p className="text-sm text-muted-foreground">
                Your Azure DevOps organization name (from dev.azure.com/your-org).
              </p>
            </div>
          )}

          {isBitbucket && (
            <div className="space-y-2">
              <Label htmlFor="email">Email{requiredMark}</Label>
              <Input
                id="email"
                data-testid="token-dialog-email"
                type="email"
                placeholder="your-email@example.com"
                {...register('email', { required: true })}
              />
              <p className="text-sm text-muted-foreground">
                The email address associated with your Atlassian account.
              </p>
            </div>
          )}

          {isJira && (
            <>
              <div className="space-y-2">
                <Label htmlFor="site">Site{requiredMark}</Label>
                <Input
                  id="site"
                  data-testid="token-dialog-site"
                  type="text"
                  placeholder="my-company"
                  {...register('site', { required: true })}
                />
                <p className="text-sm text-muted-foreground">
                  Your Jira site name (from my-company.atlassian.net).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email{requiredMark}</Label>
                <Input
                  id="email"
                  data-testid="token-dialog-email"
                  type="email"
                  placeholder="your-email@example.com"
                  {...register('email', { required: true })}
                />
                <p className="text-sm text-muted-foreground">
                  The email address associated with your Atlassian account.
                </p>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="token">{tokenLabel}{requiredMark}</Label>
            <Input
              id="token"
              data-testid="token-dialog-token"
              type="password"
              autoComplete="new-password"
              placeholder={`Paste your ${tokenLabel.toLowerCase()}`}
              {...register('token', { required: true })}
            />
            {inlineError && (
              <p role="alert" className="text-sm text-destructive">{inlineError}</p>
            )}
            {asksForConnectionName && (
              <p className="text-sm text-muted-foreground">Stored securely and set up per user.</p>
            )}
            {helpText && !inlineError && (
              <p className="text-sm text-muted-foreground">{helpText}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !isValid}>
              {isReconnect
                ? (isSaving ? 'Reconnecting...' : 'Reconnect')
                : asksForConnectionName
                  ? (<><Check className="mr-1 h-4 w-4" />{isSaving ? 'Connecting...' : 'Connect'}</>)
                  : (isSaving ? 'Connecting...' : 'Connect')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
